import { MaxSizeSpinner, SmallSpinner } from '@/components/loading_states/generic-spinner'
import { useAuth } from '@/components/providers/auth-provider'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useNetworkInfo } from '@/hooks/use-network-info'
import { envWeb } from '@/lib/env-web'
import { useTRPC } from '@/trpc/react'
import { COLORS, getDisplayEmail, getTypedEffectiveSettingValue } from '@repo/shared_utils'
import { AppRouter } from '@repo/trpc'
import { inferRouterOutputs } from '@repo/trpc/trpc-server'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps'
import {
  CalendarIcon,
  Camera,
  ChartNoAxesGantt,
  Clock,
  ExternalLink,
  Info,
  InfoIcon,
  Smartphone,
  Sparkles,
} from 'lucide-react'
import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'
import { useGeolocated } from 'react-geolocated'
import { FaAndroid, FaApple } from 'react-icons/fa'
import Webcam from 'react-webcam'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/employee/')({
  component: RouteComponent,
})

// function assert(condition: boolean, message: string): asserts condition {
//   if (!condition) {
//     throw new Error(message)
//   }
// }

function RouteComponent() {
  const api = useTRPC()
  const { data: currentTime } = useQuery({
    queryKey: ['current-time'],
    queryFn: () => DateTime.now(),
    refetchInterval: 60000, // refetch every minute (60 seconds)
    staleTime: 0,
  })

  // Network information hook
  const networkInfo = useNetworkInfo()

  // Auth user for email masking preference
  const { user: authUser } = useAuth()

  // Log network information
  console.log('🌐 Network Information:', {
    type: networkInfo.type,
    effectiveType: networkInfo.effectiveType,
    downlink: networkInfo.downlink ? `${networkInfo.downlink} Mbps` : 'Unknown',
    rtt: networkInfo.rtt ? `${networkInfo.rtt}ms` : 'Unknown',
    saveData: networkInfo.saveData,
    isOnline: networkInfo.isOnline,
    strength: networkInfo.strength,
  })

  const [overtimeStartDateTime, setOvertimeStartDateTime] = useState<Date | null>(null)
  const [overtimeDurationHours, setOvertimeDurationHours] = useState<number>(1)
  const [overtimeErrors, setOvertimeErrors] = useState<{
    start?: string
    duration?: string
    startDate?: string
  }>({})
  const [isOvertimeEnabled, setIsOvertimeEnabled] = useState(false)
  const [isEmployeeRemarksEnabled, setIsEmployeeRemarksEnabled] = useState(false)
  const [employeeRemarks, setEmployeeRemarks] = useState('')
  const [selfieImage, setSelfieImage] = useState<string | null>(null)
  const [isTakingSelfie, setIsTakingSelfie] = useState(false)
  const webcamRef = useRef<Webcam>(null)

  // Webcam video constraints
  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: 'environment',
  }

  // selfie stuff
  const startCamera = () => {
    setIsTakingSelfie(true)
  }

  const stopCamera = () => {
    setIsTakingSelfie(false)
  }

  const captureSelfie = async () => {
    // In dev mode, use sample image; otherwise use actual camera
    if (import.meta.env.MODE === 'development') {
      try {
        // Load sample image for local development
        // Files in public folder are served at root in Vite
        const sampleImagePath = '/sample_mobile_images/sample-mob-img.jpg'
        const response = await fetch(sampleImagePath)
        if (!response.ok) {
          throw new Error(`Failed to fetch sample image: ${response.statusText}`)
        }
        const blob = await response.blob()
        const reader = new FileReader()

        reader.onloadend = () => {
          const imageSrc = reader.result as string
          if (imageSrc) {
            // Convert to WebP for space efficiency
            convertToWebP(imageSrc, (webpImage) => {
              setSelfieImage(webpImage)
              stopCamera()
            })
          } else {
            toast.error('Failed to load sample image')
          }
        }

        reader.onerror = () => {
          toast.error('Failed to load sample image')
        }

        reader.readAsDataURL(blob)
      } catch (error) {
        console.error('Error loading sample image:', error)
        toast.error('Failed to load sample image')
      }
    } else {
      // Use actual camera in production
      const imageSrc = webcamRef.current?.getScreenshot()
      if (imageSrc) {
        // Convert to WebP for space efficiency
        convertToWebP(imageSrc, (webpImage) => {
          setSelfieImage(webpImage)
          stopCamera()
        })
      } else {
        toast.error('Failed to capture selfie')
      }
    }
  }

  /**
   * ⚠️ DUPLICATED LOGIC - MUST STAY IN SYNC ⚠️
   *
   * This resizing logic is duplicated in apps/mobile/app/dashboard.tsx
   * (as the calculateResizeDimensions function).
   *
   * 🔴 FOR LLM AGENTS AND HUMAN DEVELOPERS:
   * - This logic MUST ALWAYS be kept in sync with the mobile app version
   * - When modifying this resizing logic, you MUST also update apps/mobile/app/dashboard.tsx
   * - The mobile app has this as a separate calculateResizeDimensions function (around line 68-89)
   * - Both implementations must produce identical results for the same inputs
   * - The maxDimension value (540) MUST match between both apps
   * - Consider extracting to @repo/shared_utils once import issues are resolved
   *
   * Function to convert image to WebP format.
   * Resizes to ensure largest dimension is no larger than 540px.
   */
  const convertToWebP = (originalImage: string, callback: (webpImage: string) => void) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      // ⚠️ MAGIC NUMBER ALERT: 540 is hardcoded here and MUST match the mobile app (apps/mobile/app/dashboard.tsx line ~517)
      // Consider extracting to a shared constant: MAX_SELFIE_DIMENSION = 540
      const maxDimension = 540 // ⚠️ MUST stay in sync with mobile app's maxDimension value
      let { width, height } = img

      // Calculate new dimensions ensuring largest dimension is no larger than 540px
      if (width <= maxDimension && height <= maxDimension) {
        // Image is already small enough, use original dimensions
        canvas.width = width
        canvas.height = height
      } else {
        // Calculate resize maintaining aspect ratio
        const aspectRatio = width / height
        if (width > height) {
          width = maxDimension
          height = Math.round(maxDimension / aspectRatio)
        } else {
          height = maxDimension
          width = Math.round(maxDimension * aspectRatio)
        }
        canvas.width = width
        canvas.height = height
      }

      // Draw and compress the image
      ctx?.drawImage(img, 0, 0, width, height)

      // Convert to WebP with compression (0.6 = 60% quality, matching mobile app)
      const webpDataUrl = canvas.toDataURL('image/webp', 0.6)
      callback(webpDataUrl)
    }

    img.src = originalImage
  }

  const retakeSelfie = () => {
    setSelfieImage(null)
    startCamera()
  }

  // geolocation stuff
  const { coords, isGeolocationAvailable, isGeolocationEnabled } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: true,
    },
    userDecisionTimeout: 5000,
  })
  console.log({ isGeolocationEnabled, isGeolocationAvailable })

  // Add useEffect for automatic location permission check
  useEffect(() => {
    const checkAndRequestLocationPermission = async () => {
      try {
        // First check if the browser supports the Permissions API
        if (!navigator.permissions || !navigator.geolocation) {
          toast.error('Geolocation is not supported by your browser')
          return
        }

        // Query the permission status
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' })

        if (permissionStatus.state === 'prompt') {
          // If permission hasn't been granted or denied yet, request it
          toast.info('Location access is required for time tracking', {
            description: 'Please allow location access when prompted',
            duration: 5000,
          })

          // Request the position to trigger the browser's permission prompt
          navigator.geolocation.getCurrentPosition(
            () => {
              toast.success('Location access granted, try refreshing the page')
              // window.location.reload()
            },
            (error) => {
              console.error('Error getting location:', error)
              if (error.code === error.PERMISSION_DENIED) {
                toast.error(
                  'Location access was denied. Please allow location access to use time tracking features.',
                  {
                    duration: 5000,
                  },
                )
              }
            },
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0,
            },
          )
        } else if (permissionStatus.state === 'denied') {
          toast.error('Location access is blocked', {
            description:
              'Please enable location services in your browser settings to use time tracking features',
            duration: 5000,
            action: {
              label: 'How to enable?',
              onClick: () => {
                // Open instructions in a new tab based on the browser
                const userAgent = navigator.userAgent.toLowerCase()
                let helpUrl = ''

                if (userAgent.includes('chrome')) {
                  helpUrl = 'https://support.google.com/chrome/answer/142065'
                } else if (userAgent.includes('firefox')) {
                  helpUrl = 'https://support.mozilla.org/en-US/kb/site-permissions-panel'
                } else if (userAgent.includes('safari')) {
                  helpUrl = 'https://support.apple.com/guide/safari/websites-ibrw7f78f7fe/mac'
                }

                if (helpUrl) {
                  window.open(helpUrl, '_blank')
                }
              },
            },
          })
        }

        // Watch for permission changes
        const handlePermissionChange = () => {
          if (permissionStatus.state === 'granted') {
            // window.location.reload()
            toast.success('Location access granted, try refreshing the page')
          }
        }

        permissionStatus.addEventListener('change', handlePermissionChange)

        // Return cleanup function
        return () => {
          permissionStatus.removeEventListener('change', handlePermissionChange)
        }
      } catch (error) {
        console.error('Error checking location permission:', error)
        toast.error('Failed to check location permissions')
      }
    }

    // Call the async function and handle its cleanup
    const cleanupPromise = checkAndRequestLocationPermission()

    // Return cleanup function that will be called when component unmounts
    return () => {
      cleanupPromise.then((cleanup) => {
        if (cleanup) cleanup()
      })
    }
  }, []) // Empty dependency array means this runs once when component mounts

  // queries and mutations
  const { data: locationSpoofStatus } = useQuery({
    ...api.user.getMyLocationSpoofStatus.queryOptions(),
  })

  const { data: employee, isPending: isEmployeePending } = useQuery({
    ...api.employee.getEmployeeMe.queryOptions(),
  })

  const {
    data: timesheets,
    refetch,
    isPending: isTimesheetsPending,
  } = useQuery({
    ...api.timesheet.getTimesheetsMe.queryOptions({
      page: 1,
      pageSize: 30,
    }),
  })

  const { mutate: clockInTimesheetAndCreate, isPending: isClockInPending } = useMutation({
    ...api.timesheet.clockInTimesheet.mutationOptions(),
    onSuccess: (result) => {
      toast.success(
        `Clocked in successfully at ${result.clockIn ? DateTime.fromJSDate(result.clockIn).toFormat('MMM dd, hh:mm a') : ''}`,
      )
      setSelfieImage(null) // Clear the selfie after successful clock in
      refetch()
    },
    onError: (error) => {
      console.error(error)
      toast.error(`Failed to clock in at ${DateTime.now().toFormat('hh:mm a')}`)
    },
  })
  const { mutate: clockOutTimesheet, isPending: isClockOutPending } = useMutation({
    ...api.timesheet.clockOutTimesheet.mutationOptions(),
    onSuccess: (result) => {
      toast.success(
        `Clocked out successfully at ${result.clockOut ? DateTime.fromJSDate(result.clockOut).toFormat('MMM dd, hh:mm a') : ''}`,
      )
      setSelfieImage(null) // Clear the selfie after successful clock out
      refetch()
    },
    onError: (error) => {
      console.error(error)
      toast.error(`Failed to clock out at ${DateTime.now().toFormat('hh:mm a')}`)
    },
  })
  const { mutate: submitTimesheet, isPending: isSubmitTimesheetPending } = useMutation({
    ...api.timesheet.submitTimesheet.mutationOptions(),
    onSuccess: (result) => {
      toast.success(
        `Timesheet submitted successfully at ${result.date ? DateTime.fromISO(result.date).toFormat('MMM dd, hh:mm a') : ''}`,
        {
          description: 'Please wait for approval.',
        },
      )
      refetch()
    },
    onError: (error) => {
      if (error.data?.code === 'FORBIDDEN') {
        toast.error(error.message, {
          description: 'Please contact your administrator if you believe this is an error.',
        })
      } else {
        console.error(error)
      }
    },
  })
  const { data: calendarEvents } = useQuery({
    ...api.calendar.getCalendarEvents.queryOptions({
      startDate: undefined,
      endDate: undefined,
    }),
  })

  const { data: permanentBusinessSettings } = useQuery({
    ...api.permanentBusinessSettings.getPermanentBusinessSettings.queryOptions(),
  })

  const todayEvents = calendarEvents?.filter(
    (event) => event.date === new Date().toISOString().split('T')[0],
  )
  console.log({ todayEvents })

  // Effective position: use spoofed location if whitelisted and set, else use real GPS
  const spoofLocation = locationSpoofStatus?.isWhitelisted
    ? locationSpoofStatus.spoofLocation
    : undefined
  const hasSpoofLocation =
    spoofLocation?.lat !== undefined &&
    spoofLocation?.lng !== undefined &&
    !Number.isNaN(spoofLocation.lat) &&
    !Number.isNaN(spoofLocation.lng)

  const position = hasSpoofLocation
    ? { lat: spoofLocation!.lat, lng: spoofLocation!.lng }
    : { lat: coords?.latitude ?? 0, lng: coords?.longitude ?? 0 }

  // Helper function to get clock in/out requirements state
  function getClockInOutRequirementsState() {
    const requireLocation = permanentBusinessSettings?.requireLocation ?? false
    const requireSelfie = permanentBusinessSettings?.requireSelfie ?? false

    // When using spoofed location, we have a valid location; otherwise require real GPS
    const hasGeolocation = hasSpoofLocation
      ? true
      : Boolean(
          coords?.latitude && coords?.longitude && isGeolocationEnabled && isGeolocationAvailable,
        )
    const hasSelfie = !!selfieImage

    return {
      requireLocation,
      requireSelfie,
      hasGeolocation,
      hasSelfie,
    }
  }

  // Helper function to validate clock in/out requirements
  function validateClockInOutRequirements(
    action: 'clock in' | 'clock out' | 'start another shift',
  ) {
    const { requireLocation, requireSelfie, hasGeolocation, hasSelfie } =
      getClockInOutRequirementsState()

    // Validate requirements
    if (requireLocation && !hasGeolocation) {
      return {
        isValid: false,
        hasGeolocation,
        hasSelfie,
        errorMessage: `Location services are required to ${action}. Please enable location services.`,
        shouldStartCamera: false,
      }
    }

    if (requireSelfie && !hasSelfie) {
      return {
        isValid: false,
        hasGeolocation,
        hasSelfie,
        errorMessage: `A selfie is required to ${action}. Please take a selfie.`,
        shouldStartCamera: action === 'start another shift',
      }
    }

    return {
      isValid: true,
      hasGeolocation,
      hasSelfie,
    }
  }

  // function handlers
  const handleClockIn = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!employee) {
      toast.error('Employee data not yet loaded')
      return
    }

    const validation = validateClockInOutRequirements('clock in')
    if (!validation.isValid) {
      toast.error(validation.errorMessage)
      return
    }

    try {
      clockInTimesheetAndCreate({
        timeInLongitude: validation.hasGeolocation ? position.lng.toString() : undefined,
        timeInLatitude: validation.hasGeolocation ? position.lat.toString() : undefined,
        selfieImage: validation.hasSelfie ? (selfieImage ?? undefined) : undefined,
      })
    } catch (error) {
      console.error('Failed to create timesheet:', error)
    }
  }

  // Confirm and create another timesheet (continuous shift)
  const confirmStartAnotherShift = () => {
    const confirmed = window.confirm(
      'You have finished your shift for today. Do you want to start another shift and create a new timesheet? This Action will create a new timesheet with the current date and time.',
    )
    if (!confirmed) return

    const validation = validateClockInOutRequirements('start another shift')
    if (!validation.isValid) {
      if (validation.shouldStartCamera) {
        startCamera()
      }
      toast.info(validation.errorMessage)
      return
    }

    try {
      clockInTimesheetAndCreate({
        timeInLongitude: validation.hasGeolocation ? position.lng.toString() : undefined,
        timeInLatitude: validation.hasGeolocation ? position.lat.toString() : undefined,
        selfieImage: validation.hasSelfie ? (selfieImage ?? undefined) : undefined,
      })
    } catch (error) {
      console.error('Failed to create next timesheet:', error)
    }
  }

  const handleSubmitTimesheet = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('inside handleSubmitTimesheet')
    console.log({ isOvertimeEnabled, overtimeStartDateTime, overtimeDurationHours })
    if (!activeTimesheet?.timesheetId) {
      toast.error('No timesheet found for today')
      return
    }

    // Validate overtime inputs only if overtime is enabled
    if (isOvertimeEnabled) {
      const { errors, isValid } = validateOvertimeInputs(
        overtimeStartDateTime,
        overtimeDurationHours,
      )
      setOvertimeErrors(errors)

      if (!isValid) {
        console.log('invalid overtime times')
        console.log({ errors, isValid })
        toast.error('Error: Invalid overtime', {
          description: 'If you think this is an error, disable overtime then add a remark',
        })
        return
      }
    }

    try {
      console.log('submitting timesheet')
      console.log({ overtimeStartDateTime, overtimeDurationHours })
      // Normalize OT start to minute precision using Luxon
      const normalizedOvertimeStart = overtimeStartDateTime
        ? DateTime.fromJSDate(overtimeStartDateTime).set({ second: 0, millisecond: 0 }).toJSDate()
        : null

      // Compute OT end using Luxon (needed for addition), then zero seconds/ms
      const overtimeEndDateTime = normalizedOvertimeStart
        ? DateTime.fromJSDate(normalizedOvertimeStart)
            .plus({ hours: overtimeDurationHours })
            .set({ second: 0, millisecond: 0 })
            .toJSDate()
        : null
      console.log({ overtimeEndDateTime, activeTimesheet })

      submitTimesheet({
        timesheetId: activeTimesheet.timesheetId,
        overtimeStart: isOvertimeEnabled ? normalizedOvertimeStart : null,
        overtimeEnd: isOvertimeEnabled ? overtimeEndDateTime : null,
        employeeRemarks:
          isEmployeeRemarksEnabled && employeeRemarks.trim() ? employeeRemarks.trim() : null,
      })
    } catch (error) {
      console.error('Failed to submit timesheet:', error)
    }
  }

  const todayISO = DateTime.now().toFormat('yyyy-MM-dd')
  const todaysTimesheetsList = timesheets?.data?.filter((t) => t.date === todayISO) ?? []
  const todayTimesheet =
    todaysTimesheetsList.length === 0
      ? undefined
      : [...todaysTimesheetsList].sort((a, b) => {
          const aTime = a.clockIn ? new Date(a.clockIn).getTime() : 0
          const bTime = b.clockIn ? new Date(b.clockIn).getTime() : 0
          return bTime - aTime
        })[0]
  const yesterdayISO = DateTime.now().minus({ days: 1 }).toFormat('yyyy-MM-dd')
  const yesterdaysTimesheetsList = timesheets?.data?.filter((t) => t.date === yesterdayISO) ?? []
  const yesterdayTimesheet =
    yesterdaysTimesheetsList.length === 0
      ? undefined
      : [...yesterdaysTimesheetsList].sort((a, b) => {
          const aTime = a.clockIn ? new Date(a.clockIn).getTime() : 0
          const bTime = b.clockIn ? new Date(b.clockIn).getTime() : 0
          return bTime - aTime
        })[0]

  const yesterdayTimesheetWithClockInNoClockOutAndWithin24Hrs =
    yesterdayTimesheet?.clockIn &&
    !yesterdayTimesheet?.clockOut &&
    getTimeDifferenceInHrs(
      DateTime.now(),
      DateTime.fromJSDate(new Date(yesterdayTimesheet?.clockIn)),
    ) < 24

  const yesterdayTimesheetHasntBeenSubmitted =
    yesterdayTimesheet?.clockIn &&
    yesterdayTimesheet?.clockOut &&
    yesterdayTimesheet?.status === 'draft'

  const activeTimesheet =
    yesterdayTimesheetWithClockInNoClockOutAndWithin24Hrs || yesterdayTimesheetHasntBeenSubmitted
      ? yesterdayTimesheet
      : todayTimesheet
  console.log({ activeTimesheet, yesterdayTimesheet, todayTimesheet })

  const { data: todayEffectiveSettings } = useQuery({
    ...api.effectiveSettings.getOwnEffectiveSettingsForDate.queryOptions({
      // when getting the effective settings, if we have an active timesheet, then get from that
      date: activeTimesheet?.date ?? DateTime.now().toISODate(),
    }),
  })
  console.log({ todayEffectiveSettings })

  // Default overtime dates to clock-in date (similar to mobile)
  useEffect(() => {
    if (activeTimesheet?.clockIn && !overtimeStartDateTime && todayEffectiveSettings) {
      const shiftStartTime = getTypedEffectiveSettingValue(
        todayEffectiveSettings,
        'employee_shift_hours_start_time',
      )
      const dailyMaxRegHrs = getTypedEffectiveSettingValue(
        todayEffectiveSettings,
        'max_daily_reg_hrs',
      )
      console.log({ shiftStartTime, dailyMaxRegHrs })
      // ok to use fallback values here since this is just for the initial state
      const shiftStartDt = DateTime.fromFormat(shiftStartTime ?? '00:00', 'HH:mm')
      const shiftEndDt = shiftStartDt.plus({ hours: dailyMaxRegHrs ?? 8 })
      console.log({ shiftStartDt, shiftEndDt })

      const clockInDateWithShiftStartTime = DateTime.fromJSDate(activeTimesheet.clockIn)
        .set({
          hour: shiftEndDt.hour,
          minute: shiftEndDt.minute,
        })
        .toJSDate()
      setOvertimeStartDateTime(clockInDateWithShiftStartTime)
    }
  }, [activeTimesheet?.clockIn, todayEffectiveSettings])
  console.log({ overtimeStartDateTime })

  // const activeTimesheetSubject = activeTimesheet && subject('Timesheet', activeTimesheet)
  const timeElapsedSinceClockInHrs = activeTimesheet?.clockIn
    ? // ? DateTime.now().diff(DateTime.fromJSDate(new Date(activeTimesheet.clockIn)), 'hours').hours
      getTimeDifferenceInHrs(DateTime.now(), DateTime.fromJSDate(activeTimesheet.clockIn))
    : 0

  const handleClockOut = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activeTimesheet) {
      toast.error('No active timesheet found')
      return
    }

    const validation = validateClockInOutRequirements('clock out')
    if (!validation.isValid) {
      toast.error(validation.errorMessage)
      return
    }

    try {
      clockOutTimesheet({
        timesheetId: activeTimesheet.timesheetId,
        timeOutLongitude: validation.hasGeolocation ? position.lng.toString() : undefined,
        timeOutLatitude: validation.hasGeolocation ? position.lat.toString() : undefined,
        selfieImage: validation.hasSelfie ? (selfieImage ?? undefined) : undefined,
      })
    } catch (error) {
      console.error('Failed to clock out:', error)
    }
  }

  console.log({
    isClockOutPending,
    isGeolocationEnabled,
    isGeolocationAvailable,
    coords,
    activeTimesheet,
    timeElapsedSinceClockInHrs,
  })

  // Add the selfie UI component
  const SelfieCapture = () => {
    if (!isTakingSelfie && !selfieImage) {
      return (
        <Button
          variant="outline"
          onClick={startCamera}
          className="w-full rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg"
          style={{
            borderColor: COLORS[3],
            color: COLORS[3],
            backgroundColor: `${COLORS[3]}10`,
          }}
        >
          <Camera className="mr-2 h-4 w-4" />
          Take Selfie
        </Button>
      )
    }

    if (isTakingSelfie) {
      return (
        <div className="space-y-4">
          <div className="relative aspect-video w-full overflow-hidden rounded-lg border">
            <Webcam
              ref={webcamRef}
              audio={false}
              height={720}
              screenshotFormat="image/jpeg"
              width={1280}
              videoConstraints={videoConstraints}
              className="h-full w-full object-cover"
              style={{ transform: 'scaleX(-1)' }} // Mirror the video for selfie view
              mirrored={true}
              onUserMediaError={(error) => {
                console.error('Webcam error:', error)
                toast.error('Failed to access camera')
                stopCamera()
              }}
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={stopCamera}
              className="flex-1 rounded-xl font-semibold transition-all duration-300 hover:scale-105"
              style={{
                borderColor: 'rgb(156 163 175)', // gray-400
                color: 'rgb(107 114 128)', // gray-500
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={captureSelfie}
              className="flex-1 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
              style={{ backgroundColor: COLORS[3] }}
            >
              Capture
            </Button>
          </div>
        </div>
      )
    }

    if (selfieImage) {
      return (
        <div className="space-y-4">
          <div className="relative aspect-video w-full overflow-hidden rounded-lg border">
            <img src={selfieImage} alt="Selfie" className="h-full w-full object-cover" />
          </div>
          <Button
            variant="outline"
            onClick={retakeSelfie}
            className="w-full rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg"
            style={{
              borderColor: COLORS[3],
              color: COLORS[3],
              backgroundColor: `${COLORS[3]}10`,
            }}
          >
            Retake Selfie
          </Button>
        </div>
      )
    }

    return null
  }

  // Validation function for overtime times (similar to mobile)
  function validateOvertimeInputs(overtimeStartDateTime: Date | null, durationHours: number) {
    console.log('inside validateOvertimeInputs')
    console.log({ overtimeStartDateTime, durationHours })
    const errors: { start?: string; duration?: string; startDate?: string } = {}

    if (!activeTimesheet?.clockIn || !activeTimesheet?.clockOut) {
      console.log('clock in and out times not loaded')
      return { errors: { start: 'Clock in and out times required' }, isValid: false }
    }

    if (!activeTimesheet?.date) {
      console.log('timesheet date not available')
      return { errors: { start: 'Timesheet date not available' }, isValid: false }
    }

    if (!todayEffectiveSettings) {
      console.log('employee settings not loaded')
      return {
        errors: {
          start: 'Employee settings not loaded, if error persists, please contact your admin',
        },
        isValid: false,
      }
    }

    const clockOutTime = DateTime.fromJSDate(activeTimesheet.clockOut)

    // Get effective settings for the day
    const employeeShiftStartTimeStr = getTypedEffectiveSettingValue(
      todayEffectiveSettings,
      'employee_shift_hours_start_time',
    )
    const employeemaxDailyRegHrs = getTypedEffectiveSettingValue(
      todayEffectiveSettings,
      'max_daily_reg_hrs',
    )

    const employeeMaxOtHours = getTypedEffectiveSettingValue(
      todayEffectiveSettings,
      'max_daily_ot_hrs',
    )
    console.log({
      employeeShiftStartTimeStr,
      employeemaxDailyRegHrs,
      employeeMaxOtHours,
    })

    if (!employeeShiftStartTimeStr || !employeemaxDailyRegHrs || !employeeMaxOtHours) {
      console.log('shift schedule not configured')
      return {
        errors: { start: 'Shift schedule not configured, contact your admin' },
        isValid: false,
      }
    }

    if (overtimeStartDateTime) {
      const otStartDateTimeLuxon = DateTime.fromJSDate(overtimeStartDateTime).set({
        second: 0,
        millisecond: 0,
      })
      const otCalculatedEndDateTime = otStartDateTimeLuxon
        .plus({ hours: durationHours })
        .set({ second: 0, millisecond: 0 })
      console.log({ otStartDateTimeLuxon, otCalculatedEndDateTime })

      try {
        // Calculate the employee's shift end time based on effective settings
        const employeeShiftStartTime = DateTime.fromFormat(employeeShiftStartTimeStr, 'HH:mm')
        if (!employeeShiftStartTime.isValid) {
          return { errors: { start: 'Invalid shift start time configuration' }, isValid: false }
        }

        // Parse the timesheet date (same approach as backend)
        const timesheetDate = DateTime.fromFormat(activeTimesheet.date, 'yyyy-MM-dd')
        if (!timesheetDate.isValid) {
          return {
            errors: { start: 'Invalid timesheet date, contact your admin' },
            isValid: false,
          }
        }

        const shiftStartOnDate = timesheetDate.set({
          hour: employeeShiftStartTime.hour,
          minute: employeeShiftStartTime.minute,
          second: 0,
          millisecond: 0,
        })

        // Calculate when regular work ends (shift start + max daily regular hours)
        const regularWorkEnd = shiftStartOnDate.plus({ hours: employeemaxDailyRegHrs })

        console.log('... debugging ...', {
          regularWorkEnd,
          otStartDateTimeLuxon,
          shiftStartOnDate,
        })
        // OT start cannot be earlier than the employee's shift end time
        if (otStartDateTimeLuxon < regularWorkEnd) {
          console.log(
            'validation error: ot start cannot be earlier than the regular work end time',
          )
          errors.start = `OT start cannot be earlier than ${regularWorkEnd.toFormat('MMM d, yyyy • hh:mm a')} (end of regular shift)`
        }

        // Validate duration
        if (durationHours <= 0) {
          console.log('validation error: duration must be greater than 0')
          errors.duration = 'Duration must be greater than 0'
        }

        if (durationHours > employeeMaxOtHours) {
          console.log('validation error: duration cannot exceed max OT hours')
          errors.duration = `Duration cannot exceed ${employeeMaxOtHours} hours (max OT hours)`
        }

        // OT end cannot exceed clock out time
        if (otCalculatedEndDateTime > clockOutTime) {
          console.log('validation error: ot end cannot exceed clock out time')
          errors.duration = `OT end (${otCalculatedEndDateTime.toFormat('hh:mm:ss a')}) cannot exceed clock out time (${clockOutTime.toFormat('hh:mm:ss a')})`
        }

        console.log({
          otStartDateTimeLuxon,
          otCalculatedEndDateTime,
          regularWorkEnd,
          clockOutTime,
          durationHours,
          employeeMaxOtHours,
          errors,
        })
      } catch (error) {
        console.error('Error validating overtime times:', error)
        return { errors: { start: 'Error validating overtime times' }, isValid: false }
      }
    }

    return { errors, isValid: Object.keys(errors).length === 0 }
  }

  // employee shift start and end time
  const maxDailyRegHrs = getTypedEffectiveSettingValue(todayEffectiveSettings, 'max_daily_reg_hrs')

  if (isEmployeePending || isTimesheetsPending)
    return (
      <div className="flex w-full h-full min-h-screen items-center justify-center">
        <MaxSizeSpinner />
      </div>
    )

  const isEmployeeResigned = employee?.isEmployeeResigned === true
  const isEmployeeVerifiedByAdmin = employee?.isEmployeeVerifiedByAdmin
  console.log({ isEmployeeResigned, isEmployeeVerifiedByAdmin })
  if (isEmployeeResigned) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-rose-50 dark:from-slate-950 dark:via-slate-900 dark:to-rose-950 p-4 md:p-8 pt-6">
        <div className="relative">
          <div className="flex items-center justify-between space-y-2 mb-6">
            <div className="flex flex-col space-y-1">
              <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-gray-900 via-red-900 to-rose-900 dark:from-white dark:via-red-200 dark:to-rose-200 bg-clip-text text-transparent">
                Account Deactivated
              </h2>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="flex justify-center">
            <Card className="w-full max-w-2xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-0 shadow-2xl ring-1 ring-gray-200 dark:ring-gray-800">
              <CardContent className="flex flex-col items-center justify-center space-y-6 py-16 relative">
                <div className="rounded-full p-8 transition-all duration-300 hover:scale-110 bg-red-100 dark:bg-red-900/30">
                  <Info className="h-16 w-16 text-red-500" />
                </div>
                <div className="text-center space-y-4">
                  <h3 className="text-3xl font-bold tracking-tight">
                    <span className="bg-gradient-to-r from-red-600 via-rose-600 to-pink-600 bg-clip-text text-transparent">
                      This account has been deactivated
                    </span>
                  </h3>
                  <p className="text-center text-lg text-muted-foreground max-w-md leading-relaxed">
                    If you believe this is a mistake, please contact your administrator.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  // if (isEmployeeVerifiedByAdmin === false) {
  if (!isEmployeeVerifiedByAdmin) {
    // this is probably ok since we check for a pending state anyway (i.e. no need to check for false)
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 dark:from-slate-950 dark:via-slate-900 dark:to-orange-950 p-4 md:p-8 pt-6">
        {/* Background decorative elements */}
        <div
          className="absolute top-20 right-20 w-64 h-64 rounded-full opacity-10 blur-3xl"
          style={{ backgroundColor: COLORS[3] }}
        />
        <div
          className="absolute bottom-20 left-20 w-64 h-64 rounded-full opacity-10 blur-3xl"
          style={{ backgroundColor: COLORS[1] }}
        />

        <div className="relative">
          <div className="flex items-center justify-between space-y-2 mb-6">
            <div className="flex flex-col space-y-1">
              <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-gray-900 via-orange-900 to-amber-900 dark:from-white dark:via-orange-200 dark:to-amber-200 bg-clip-text text-transparent">
                Dashboard
              </h2>
              {employee?.firstName && (
                <p className="text-lg text-muted-foreground">
                  Welcome, {employee.firstName}
                  {employee.lastName && ` ${employee.lastName}`}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border border-gray-200 dark:border-gray-800 shadow-lg">
              <CalendarIcon className="mr-2 h-4 w-4" style={{ color: COLORS[1] }} />
              <span className="text-sm text-muted-foreground">
                {currentTime?.toFormat('EEEE, MMMM d, yyyy • h:mm a')}
              </span>
            </div>
          </div>

          <Separator className="my-4" />

          <div className="flex justify-center">
            <Card className="w-full max-w-2xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-0 shadow-2xl ring-1 ring-gray-200 dark:ring-gray-800">
              <CardContent className="flex flex-col items-center justify-center space-y-6 py-16 relative">
                <div
                  className="rounded-full p-8 transition-all duration-300 hover:scale-110"
                  style={{ backgroundColor: `${COLORS[3]}15` }}
                >
                  <Clock className="h-16 w-16" style={{ color: COLORS[3] }} />
                </div>
                <div className="text-center space-y-4">
                  <h3 className="text-3xl font-bold tracking-tight">
                    <span className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent">
                      Account Pending Verification
                    </span>
                  </h3>
                  <div
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: `${COLORS[3]}10`,
                      color: COLORS[3],
                      border: `1px solid ${COLORS[3]}20`,
                    }}
                  >
                    <Sparkles className="h-4 w-4" />
                    Verification in Progress
                  </div>
                  <p className="text-center text-lg text-muted-foreground max-w-md leading-relaxed">
                    Your account is currently awaiting verification from an administrator.
                    You&apos;ll have full access to the dashboard once verified. Once you are
                    verified, you might have to log out and log in again.
                  </p>
                </div>
                {employee?.email && (
                  <div className="absolute bottom-4 left-12 text-sm text-muted-foreground">
                    {getDisplayEmail(employee.email, authUser?.maskEmail ?? false)}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  // if verified by admin, show the dashboard
  return (
    <div className="flex-1 space-y-4 md:p-8 pt-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight bg-gradient-to-r from-gray-900 via-indigo-900 to-purple-900 dark:from-white dark:via-indigo-200 dark:to-purple-200 bg-clip-text text-transparent">
            Dashboard
          </h2>
          {employee?.firstName && (
            <p className="text-lg text-muted-foreground">
              Welcome, {employee.firstName}
              {employee.lastName && ` ${employee.lastName}`}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border border-gray-200 dark:border-gray-800 shadow-lg">
          <CalendarIcon className="mr-2 h-4 w-4" style={{ color: COLORS[0] }} />
          <span className="text-sm text-muted-foreground">{currentTime?.toFormat('EEEE')}</span>
        </div>
      </div>
      <Separator className="my-4" />
      {/* Two-up row on desktop: Mobile App + Today’s Schedule */}
      <div className="flex flex-col md:flex-row md:items-stretch gap-4 mb-6">
        {/* Mobile App Download Section */}
        <Card className="flex-1 flex flex-col bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border shadow-lg hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" style={{ color: COLORS[1] }} />
              Mobile App
              <span className="inline-flex ml-auto items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                Official
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="md:mt-auto">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Current Android Download */}
              <a
                href="https://play.google.com/store/apps/details?id=com.cubicpayroll.mobile.prod"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 px-6 py-3 rounded-xl bg-purple-600 text-white hover:bg-purple-700 transition-all duration-300 hover:scale-105 shadow-lg"
              >
                <FaAndroid className="h-5 w-5" />
                <div className="text-left">
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
                <ExternalLink className="h-4 w-4 ml-1" />
              </a>

              {/* iOS App Store Download */}
              <a
                href="https://apps.apple.com/us/app/cubicpayroll/id6751583963"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 px-6 py-3 rounded-xl bg-gray-900 text-white hover:bg-gray-800 transition-all duration-300 hover:scale-105 shadow-lg"
              >
                <FaApple className="h-5 w-5" />
                <div className="text-left">
                  <div className="text-sm font-semibold">App Store</div>
                </div>
                <ExternalLink className="h-4 w-4 ml-1" />
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Today’s Schedule Card */}
        <Card className="flex-1 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border shadow-lg hover:shadow-xl transition-all duration-300">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" style={{ color: COLORS[0] }} />
              {activeTimesheet?.date
                ? DateTime.fromISO(activeTimesheet.date).toFormat('yyyy-MM-dd') + ' Schedule'
                : 'Today Schedule'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayEffectiveSettings ? (
              <div className="space-y-3">
                {/* Regular Hours */}
                <div className="space-y-1">
                  <h4 className="text-xs font-medium text-muted-foreground">Regular Hours</h4>
                  <div className="text-sm font-mono">
                    {parseAndFormatTime(
                      getTypedEffectiveSettingValue(
                        todayEffectiveSettings,
                        'employee_shift_hours_start_time',
                      ),
                    )}{' '}
                    -{' '}
                    {(() => {
                      const startTimeStr = getTypedEffectiveSettingValue(
                        todayEffectiveSettings,
                        'employee_shift_hours_start_time',
                      )
                      const maxHours = getTypedEffectiveSettingValue(
                        todayEffectiveSettings,
                        'max_daily_reg_hrs',
                      )

                      if (!startTimeStr || !maxHours) return 'Not set'

                      try {
                        const startTime = DateTime.fromFormat(startTimeStr, 'HH:mm')
                        if (!startTime.isValid) return 'Not set'

                        const endTime = startTime.plus({ hours: maxHours })
                        return endTime.toFormat('hh:mm a')
                      } catch {
                        return 'Not set'
                      }
                    })()}
                  </div>
                </div>

                {/* Overtime Hours */}
                <div className="space-y-1">
                  <h4 className="text-xs font-medium text-muted-foreground">
                    Overtime Hours (if any)
                  </h4>
                  <div className="text-sm font-mono">
                    {(() => {
                      const startTimeStr = getTypedEffectiveSettingValue(
                        todayEffectiveSettings,
                        'employee_shift_hours_start_time',
                      )
                      const maxRegHours = getTypedEffectiveSettingValue(
                        todayEffectiveSettings,
                        'max_daily_reg_hrs',
                      )
                      const maxOtHours = getTypedEffectiveSettingValue(
                        todayEffectiveSettings,
                        'max_daily_ot_hrs',
                      )

                      if (!startTimeStr || !maxRegHours || !maxOtHours) return 'Not set - Not set'

                      try {
                        const startTime = DateTime.fromFormat(startTimeStr, 'HH:mm')
                        if (!startTime.isValid) return 'Not set - Not set'

                        const otStartTime = startTime.plus({ hours: maxRegHours })
                        const otEndTime = otStartTime.plus({ hours: maxOtHours })

                        return `${otStartTime.toFormat('hh:mm a')} - ${otEndTime.toFormat('hh:mm a')}`
                      } catch {
                        return 'Not set - Not set'
                      }
                    })()}
                  </div>
                </div>

                <div className="text-xs text-muted-foreground">
                  Missing or incorrect info? Contact your admin.
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Loading schedule...</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Time Tracking Section */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border shadow-lg hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" style={{ color: COLORS[0] }} />
                Time Tracking
              </CardTitle>
              {/* Timesheet Status Indicator */}
              <div className="flex items-center justify-center">
                <div
                  className="px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 shadow-lg backdrop-blur-sm border"
                  style={{
                    backgroundColor:
                      !activeTimesheet?.clockIn && !activeTimesheet?.clockOut
                        ? `${COLORS[4]}20`
                        : activeTimesheet?.clockIn && !activeTimesheet?.clockOut
                          ? `${COLORS[0]}20`
                          : activeTimesheet?.clockIn &&
                              activeTimesheet?.clockOut &&
                              !activeTimesheet?.status
                            ? `${COLORS[2]}20`
                            : activeTimesheet?.status === 'draft'
                              ? `${COLORS[4]}20`
                              : activeTimesheet?.status === 'submitted'
                                ? `${COLORS[0]}20`
                                : activeTimesheet?.status === 'approved'
                                  ? `${COLORS[2]}20`
                                  : 'rgb(254 242 242)', // red-50 equivalent for rejected
                    color:
                      !activeTimesheet?.clockIn && !activeTimesheet?.clockOut
                        ? COLORS[4]
                        : activeTimesheet?.clockIn && !activeTimesheet?.clockOut
                          ? COLORS[0]
                          : activeTimesheet?.clockIn &&
                              activeTimesheet?.clockOut &&
                              !activeTimesheet?.status
                            ? COLORS[2]
                            : activeTimesheet?.status === 'draft'
                              ? COLORS[4]
                              : activeTimesheet?.status === 'submitted'
                                ? COLORS[0]
                                : activeTimesheet?.status === 'approved'
                                  ? COLORS[2]
                                  : 'rgb(185 28 28)', // red-700 equivalent for rejected
                    borderColor:
                      !activeTimesheet?.clockIn && !activeTimesheet?.clockOut
                        ? `${COLORS[4]}40`
                        : activeTimesheet?.clockIn && !activeTimesheet?.clockOut
                          ? `${COLORS[0]}40`
                          : activeTimesheet?.clockIn &&
                              activeTimesheet?.clockOut &&
                              !activeTimesheet?.status
                            ? `${COLORS[2]}40`
                            : activeTimesheet?.status === 'draft'
                              ? `${COLORS[4]}40`
                              : activeTimesheet?.status === 'submitted'
                                ? `${COLORS[0]}40`
                                : activeTimesheet?.status === 'approved'
                                  ? `${COLORS[2]}40`
                                  : 'rgb(252 165 165)', // red-300 equivalent for rejected
                  }}
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor:
                        !activeTimesheet?.clockIn && !activeTimesheet?.clockOut
                          ? COLORS[4]
                          : activeTimesheet?.clockIn && !activeTimesheet?.clockOut
                            ? COLORS[0]
                            : activeTimesheet?.clockIn &&
                                activeTimesheet?.clockOut &&
                                !activeTimesheet?.status
                              ? COLORS[2]
                              : activeTimesheet?.status === 'draft'
                                ? COLORS[4]
                                : activeTimesheet?.status === 'submitted'
                                  ? COLORS[0]
                                  : activeTimesheet?.status === 'approved'
                                    ? COLORS[2]
                                    : 'rgb(239 68 68)', // red-500 equivalent for rejected
                    }}
                  />
                  <span className="capitalize">
                    {!activeTimesheet?.clockIn && !activeTimesheet?.clockOut && 'Not Started'}
                    {activeTimesheet?.clockIn &&
                      activeTimesheet?.clockOut &&
                      !activeTimesheet?.status &&
                      'Ready to Submit'}
                    {activeTimesheet?.status === 'draft' && 'Draft'}
                    {activeTimesheet?.status === 'submitted' && 'Submitted'}
                    {activeTimesheet?.status === 'approved' && 'Approved'}
                    {activeTimesheet?.status === 'rejected' && 'Rejected'}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {/* Finished shift CTA for creating another timesheet */}
            {activeTimesheet?.clockIn &&
              activeTimesheet?.clockOut &&
              activeTimesheet?.status === 'submitted' &&
              // only show the button if there are less than 2 timesheets for today
              todaysTimesheetsList.length < 2 && (
                <div
                  className="rounded-lg border p-4 mb-6 text-sm"
                  style={{
                    borderColor: `${COLORS[0]}40`,
                    backgroundColor: `${COLORS[0]}08`,
                  }}
                >
                  <div className="flex items-center justify-between gap-3 flex-col md:flex-row">
                    <div className="text-muted-foreground">
                      You have finished your shift for today. If you need to continue working, you
                      can create another timesheet.
                    </div>
                    <Button
                      size="sm"
                      className="rounded-xl font-semibold"
                      style={{ backgroundColor: COLORS[0] }}
                      onClick={confirmStartAnotherShift}
                    >
                      Start Another Shift
                    </Button>
                  </div>
                </div>
              )}
            <div className="flex flex-col space-y-6">
              {/* Regular Hours Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Recorded Hours</h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">
                      Time In:{' '}
                      {activeTimesheet?.clockIn
                        ? DateTime.fromJSDate(activeTimesheet?.clockIn).toFormat(
                            'MMM d, yyyy • hh:mm a',
                          )
                        : 'N/A'}
                    </p>
                    <p className="text-sm font-medium">
                      Time Out:{' '}
                      {activeTimesheet?.clockOut
                        ? DateTime.fromJSDate(activeTimesheet?.clockOut).toFormat(
                            'MMM d, yyyy • hh:mm a',
                          )
                        : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-4">
                  {(!activeTimesheet || activeTimesheet.clockIn == null) && (
                    <div className="space-y-4">
                      {(() => {
                        const { requireLocation, requireSelfie, hasGeolocation } =
                          getClockInOutRequirementsState()

                        // Show selfie capture if:
                        // 1. Selfie is required, OR
                        // 2. Location is not available and selfie is not required (fallback option)
                        const shouldShowSelfie =
                          requireSelfie || (!hasGeolocation && !requireLocation && !requireSelfie)

                        return shouldShowSelfie ? <SelfieCapture /> : null
                      })()}
                      <Button
                        className="ml-auto rounded-xl px-8 py-3 font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                        onClick={handleClockIn}
                        size="lg"
                        style={{ backgroundColor: COLORS[0] }}
                        disabled={
                          isClockInPending ||
                          isClockOutPending ||
                          isTimesheetsPending ||
                          isEmployeePending
                        }
                      >
                        {isClockInPending && <SmallSpinner />}
                        Time In
                      </Button>
                    </div>
                  )}

                  {activeTimesheet?.clockIn &&
                    activeTimesheet?.status === 'draft' &&
                    !activeTimesheet.clockOut && (
                      <div className="space-y-4">
                        {(() => {
                          const { requireLocation, requireSelfie, hasGeolocation } =
                            getClockInOutRequirementsState()

                          // Show selfie capture if:
                          // 1. Selfie is required, OR
                          // 2. Location is not available and selfie is not required (fallback option)
                          const shouldShowSelfie =
                            requireSelfie ||
                            (!hasGeolocation && !requireLocation && !requireSelfie)

                          return shouldShowSelfie ? <SelfieCapture /> : null
                        })()}

                        <Button
                          onClick={handleClockOut}
                          size="lg"
                          className="rounded-xl px-8 py-3 font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                          style={{ backgroundColor: COLORS[1] }}
                          disabled={
                            isClockInPending ||
                            isClockOutPending ||
                            // !isGeolocationEnabled ||
                            // !isGeolocationAvailable ||
                            // !coords?.latitude ||
                            (activeTimesheet?.clockIn && timeElapsedSinceClockInHrs
                              ? timeElapsedSinceClockInHrs < 0.25 // 1 minute = 0.016666 hours, 0.25 = 15mins
                              : false) ||
                            !activeTimesheet
                          }
                        >
                          {isClockOutPending && <SmallSpinner />}
                          Time Out
                        </Button>
                      </div>
                    )}
                </div>
              </div>

              {(() => {
                const { requireLocation, requireSelfie } = getClockInOutRequirementsState()

                let message = ''
                if (requireLocation && requireSelfie) {
                  message = 'Both location services and a selfie are required to clock in/out.'
                } else if (requireLocation) {
                  message = 'Location services are required to clock in/out.'
                } else if (requireSelfie) {
                  message = 'A selfie is required to clock in/out.'
                } else {
                  message = 'Having trouble with location? Use a selfie to time in/out.'
                }

                return (
                  <div
                    className="rounded-lg border p-4 text-sm transition-all duration-300 hover:shadow-lg"
                    style={{
                      borderColor: `${COLORS[0]}40`,
                      backgroundColor: `${COLORS[0]}08`,
                      color: COLORS[0],
                    }}
                  >
                    <div className="flex items-start gap-2">
                      <InfoIcon className="h-4 w-4 shrink-0" style={{ color: COLORS[0] }} />
                      <p>{message}</p>
                    </div>
                  </div>
                )
              })()}

              <Separator />

              {/* Overtime Hours Section */}
              {activeTimesheet?.clockIn &&
                activeTimesheet?.clockOut &&
                activeTimesheet?.status === 'draft' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold">Overtime Hours</h3>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="text-sm text-muted-foreground">
                        Do you have overtime hours?
                      </div>
                      <Switch
                        checked={isOvertimeEnabled}
                        onCheckedChange={(value) => {
                          setIsOvertimeEnabled(value)
                          if (!value) {
                            // Clear overtime data when disabling
                            setOvertimeStartDateTime(null)
                            setOvertimeDurationHours(1)
                            setOvertimeErrors({})
                          }
                        }}
                        disabled={!isOvertimePossible(activeTimesheet, maxDailyRegHrs ?? 0)}
                      />
                    </div>

                    {isOvertimeEnabled ? (
                      <div className="space-y-4">
                        {/* Overtime Start */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-medium">Overtime Start</h4>

                          {/* Date Picker */}
                          <div className="space-y-2">
                            <label className="text-sm text-muted-foreground">Date</label>
                            <Input
                              type="date"
                              value={
                                overtimeStartDateTime
                                  ? DateTime.fromJSDate(overtimeStartDateTime).toFormat(
                                      'yyyy-MM-dd',
                                    )
                                  : ''
                              }
                              onChange={(e) => {
                                if (e.target.value) {
                                  // Preserve the time component if it exists
                                  const currentTime = overtimeStartDateTime
                                    ? DateTime.fromJSDate(overtimeStartDateTime)
                                    : DateTime.now()
                                  const newDateTime = DateTime.fromFormat(
                                    e.target.value,
                                    'yyyy-MM-dd',
                                  ).set({
                                    hour: currentTime.hour,
                                    minute: currentTime.minute,
                                  })
                                  setOvertimeStartDateTime(newDateTime.toJSDate())
                                  if (overtimeErrors.startDate) {
                                    setOvertimeErrors((prev) => ({
                                      ...prev,
                                      startDate: undefined,
                                    }))
                                  }
                                }
                              }}
                              className={overtimeErrors.startDate ? 'border-red-500' : ''}
                            />
                            {overtimeErrors.startDate && (
                              <p className="text-red-500 text-sm">{overtimeErrors.startDate}</p>
                            )}
                          </div>

                          {/* Time Picker */}
                          <div className="space-y-2">
                            <label className="text-sm text-muted-foreground">Time</label>
                            <Input
                              type="time"
                              value={
                                overtimeStartDateTime
                                  ? DateTime.fromJSDate(overtimeStartDateTime).toFormat('HH:mm')
                                  : ''
                              }
                              onChange={(e) => {
                                if (e.target.value) {
                                  // Preserve the date component
                                  const currentDate = overtimeStartDateTime
                                    ? DateTime.fromJSDate(overtimeStartDateTime)
                                    : DateTime.now()
                                  const [hours, minutes] = e.target.value.split(':').map(Number)
                                  const newDateTime = currentDate.set({
                                    hour: hours,
                                    minute: minutes,
                                  })
                                  setOvertimeStartDateTime(newDateTime.toJSDate())
                                  if (overtimeErrors.start) {
                                    setOvertimeErrors((prev) => ({ ...prev, start: undefined }))
                                  }
                                }
                              }}
                              className={overtimeErrors.start ? 'border-red-500' : ''}
                            />
                            {overtimeErrors.start && (
                              <p className="text-red-500 text-sm">{overtimeErrors.start}</p>
                            )}
                          </div>

                          <p className="text-xs text-muted-foreground">
                            Cannot be earlier than the end of your regular shift
                          </p>
                        </div>

                        {/* Overtime Duration */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-medium">Overtime Duration (Hours)</h4>
                          <div className="flex items-center justify-between bg-muted rounded-lg p-4">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setOvertimeDurationHours(
                                  Math.max(0.5, overtimeDurationHours - 0.5),
                                )
                              }
                              style={{ backgroundColor: COLORS[2] }}
                              className="text-white border-0 hover:opacity-80"
                            >
                              -
                            </Button>

                            <span className="text-lg font-bold">
                              {overtimeDurationHours} hour{overtimeDurationHours !== 1 ? 's' : ''}
                            </span>

                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const maxOtHours =
                                  getTypedEffectiveSettingValue(
                                    todayEffectiveSettings,
                                    'max_daily_ot_hrs',
                                  ) || 8
                                setOvertimeDurationHours(
                                  Math.min(maxOtHours, overtimeDurationHours + 0.5),
                                )
                              }}
                              style={{ backgroundColor: COLORS[2] }}
                              className="text-white border-0 hover:opacity-80"
                            >
                              +
                            </Button>
                          </div>

                          {overtimeErrors.duration && (
                            <>
                              <p className="text-red-500 text-xs">{overtimeErrors.duration}</p>
                              <p className="text-red-500 text-sm">
                                Add &quot;Remarks&quot; if you think this is an error{' '}
                              </p>
                            </>
                          )}

                          {/* Preview of calculated end time */}
                          {overtimeStartDateTime && (
                            <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-4">
                              <p className="text-blue-800 dark:text-blue-200 text-sm font-medium mb-1">
                                Preview
                              </p>
                              <p className="text-blue-900 dark:text-blue-100 font-semibold">
                                OT End Time:{' '}
                                {DateTime.fromJSDate(overtimeStartDateTime)
                                  .plus({ hours: overtimeDurationHours })
                                  .set({ second: 0, millisecond: 0 })
                                  .toFormat('MMM d • hh:mm:ss a')}
                              </p>
                            </div>
                          )}

                          <p className="text-xs text-muted-foreground">
                            Duration is added to OT start time to calculate OT end time
                          </p>
                        </div>

                        {/* Clear overtime button */}
                        {overtimeStartDateTime && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setOvertimeStartDateTime(null)
                              setOvertimeDurationHours(1)
                              setOvertimeErrors({})
                            }}
                            className="w-full"
                          >
                            Reset Overtime Times
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="bg-muted rounded-lg p-6 text-center">
                        <p className="text-muted-foreground text-sm">
                          Enable overtime tracking above to record overtime hours
                        </p>
                      </div>
                    )}
                  </div>
                )}

              {/* Employee Remarks Section */}
              {activeTimesheet?.clockIn &&
                activeTimesheet?.clockOut &&
                activeTimesheet?.status === 'draft' && (
                  <>
                    <Separator />

                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold">Additional Remarks</h3>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="text-sm text-muted-foreground">
                          Do you have any additional remarks or correction requests?
                        </div>
                        <Switch
                          checked={isEmployeeRemarksEnabled}
                          onCheckedChange={(value) => {
                            setIsEmployeeRemarksEnabled(value)
                            if (!value) {
                              // Clear remarks when disabling
                              setEmployeeRemarks('')
                            }
                          }}
                        />
                      </div>

                      {isEmployeeRemarksEnabled ? (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-sm text-muted-foreground">
                              Please provide any additional remarks or notes about your work today
                            </label>
                            <Textarea
                              value={employeeRemarks}
                              onChange={(e) => setEmployeeRemarks(e.target.value)}
                              placeholder="Enter your remarks here..."
                              className="min-h-[120px] resize-none"
                              maxLength={500}
                            />
                            <div className="flex justify-end">
                              <span className="text-xs text-muted-foreground">
                                {employeeRemarks.length}/500 characters
                              </span>
                            </div>
                          </div>

                          {/* Clear remarks button */}
                          {employeeRemarks.trim() && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setEmployeeRemarks('')
                              }}
                              className="w-full"
                            >
                              Clear Remarks
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="bg-muted rounded-lg p-6 text-center">
                          <p className="text-muted-foreground text-sm">
                            Enable remarks above to add additional notes about your work today
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}

              {activeTimesheet?.clockIn &&
                activeTimesheet?.clockOut &&
                activeTimesheet?.status === 'draft' && (
                  <Button
                    onClick={handleSubmitTimesheet}
                    className="w-full rounded-xl py-4 font-semibold transition-all duration-1000 transform hover:scale-105 shadow-lg hover:shadow-xl relative overflow-hidden animate-pulse-scale"
                    style={{ backgroundColor: COLORS[2] }}
                    disabled={isSubmitTimesheetPending}
                  >
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 animate-gradient-border opacity-75"></div>
                    <div className="relative z-10 flex items-center justify-center gap-2">
                      {isSubmitTimesheetPending && <SmallSpinner />}
                      Submit Timesheet for Approval
                    </div>
                  </Button>
                )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border shadow-lg hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" style={{ color: COLORS[1] }} />
              {(() => {
                const { requireLocation, requireSelfie } = getClockInOutRequirementsState()

                if (requireLocation && requireSelfie) {
                  return 'Both location services and a selfie are required for time in/out'
                } else if (requireLocation) {
                  return 'Location services are required for time in/out'
                } else if (requireSelfie) {
                  return 'A selfie is required for time in/out'
                } else {
                  return 'Geolocation must be enabled in order for time in/out to work'
                }
              })()}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col space-y-2">
            {position.lat && position.lng && (
              <APIProvider apiKey={envWeb.VITE_PUBLIC_MAPS_API_KEY}>
                <Map
                  style={{
                    width: '100%',
                    height: '30vh',
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                  }}
                  defaultCenter={{
                    // lat: position.lat - 0.007,
                    // lng: position.lng - 0.007,
                    lat: position.lat,
                    lng: position.lng,
                  }}
                  defaultZoom={15}
                  gestureHandling={'cooperative'}
                  disableDefaultUI={true}
                  mapId={'map'}
                >
                  <AdvancedMarker
                    position={{
                      // lat: position.lat - 0.007,
                      // lng: position.lng - 0.007,
                      lat: position.lat,
                      lng: position.lng,
                    }}
                  />
                </Map>
              </APIProvider>
            )}
            {!hasSpoofLocation &&
              (!isGeolocationEnabled || !isGeolocationAvailable || !coords?.latitude) && (
                <div className="flex flex-col items-center justify-center space-y-4 p-6 text-center border-2 border-dashed border-destructive/50 rounded-lg bg-destructive/5">
                  <div className="p-3 rounded-full bg-destructive/10">
                    <Info className="h-6 w-6 text-destructive" />
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-destructive">Geolocation Required</h4>
                    <p className="text-sm text-muted-foreground max-w-[250px]">
                      If already enabled, please wait for a few seconds or refresh the page.
                    </p>
                    {!isGeolocationEnabled && (
                      <p className="text-xs text-destructive mt-2">
                        Location services are currently disabled
                      </p>
                    )}
                    {!isGeolocationAvailable && (
                      <p className="text-xs text-destructive mt-2">
                        Your browser doesn&apos;t support geolocation
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl font-semibold transition-all duration-300 hover:scale-105"
                    style={{
                      borderColor: COLORS[1],
                      color: COLORS[1],
                      backgroundColor: `${COLORS[1]}10`,
                    }}
                    onClick={() => {
                      window.location.reload()
                    }}
                  >
                    Refresh Page
                  </Button>
                </div>
              )}

            <div className="mt-4 w-full">
              <div
                className="border rounded-xl p-4 mx-auto transition-all duration-300 hover:shadow-lg"
                style={{
                  backgroundColor: `${COLORS[1]}10`,
                  borderColor: `${COLORS[1]}30`,
                }}
              >
                <div className="flex items-start gap-6">
                  <Info className="h-8 w-8 mt-0.5" style={{ color: COLORS[1] }} />
                  <p className="text-sm" style={{ color: COLORS[1] }}>
                    To <span className="font-semibold">minimize geolocation issues</span>, keep
                    location services enabled at all times, or turn it on at least 10 minutes
                    before using the app.
                  </p>
                </div>
              </div>
            </div>

            {/* Location Services Guide */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border rounded-xl p-4 hover:shadow-lg transition-all duration-300"
                style={{ borderColor: `${COLORS[0]}30` }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-full" style={{ backgroundColor: `${COLORS[0]}15` }}>
                    <FaApple className="h-5 w-5" style={{ color: COLORS[0] }} />
                  </div>
                  <h4 className="font-semibold">iOS Guide</h4>
                </div>
                <ol className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">1.</span>
                    Open <span className="font-medium text-foreground">Settings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">2.</span>
                    Tap <span className="font-medium text-foreground">Privacy & Security</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">3.</span>
                    Select <span className="font-medium text-foreground">Location Services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">4.</span>
                    Find your browser (Chrome) and select &quot;While Using the app&quot;
                  </li>
                </ol>
                <div className="mt-4">
                  <a
                    href="https://www.notion.so/iOS-Guide-1e4d5fe8ddf2809cb907f0a436299a3c"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm hover:underline transition-colors"
                    style={{ color: COLORS[0] }}
                  >
                    <ExternalLink className="h-4 w-4" />
                    Click here to view with images
                  </a>
                </div>
              </div>

              <div
                className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border rounded-xl p-4 hover:shadow-lg transition-all duration-300"
                style={{ borderColor: `${COLORS[1]}30` }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-full" style={{ backgroundColor: `${COLORS[1]}15` }}>
                    <FaAndroid className="h-5 w-5" style={{ color: COLORS[1] }} />
                  </div>
                  <h4 className="font-semibold">Android Guide</h4>
                </div>
                <ol className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">1.</span>
                    Open <span className="font-medium text-foreground">Settings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">2.</span>
                    Tap <span className="font-medium text-foreground">Location</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">3.</span>
                    Enable <span className="font-medium text-foreground">Use location</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-foreground">4.</span>
                    Find your browser (Chrome) under App permissions and allow location access
                  </li>
                </ol>
              </div>
            </div>

            <div className="h-0.5 my-4 bg-gradient-to-r from-transparent via-muted-foreground to-transparent" />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border shadow-lg hover:shadow-xl transition-all duration-300 mb-6">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" style={{ color: COLORS[3] }} />
              Today&apos;s Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayEvents && todayEvents.length > 0 ? (
              <div className="space-y-2">
                {todayEvents.map((event) => (
                  <div key={event?.eventId}>
                    <div className="font-bold flex items-center gap-2">
                      <ChartNoAxesGantt className="h-4 w-4 text-muted-foreground" />
                      {event?.name}
                    </div>
                    <p className="text-xs text-muted-foreground">{event?.category.name}</p>
                    <p className="text-xs text-muted-foreground">{event?.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No events scheduled for today</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

// time 2 occurs after time 1
function getTimeDifferenceInHrs(time1: DateTime, time2: DateTime) {
  return time1.diff(time2, 'hours').hours
}

type TimesheetMe = inferRouterOutputs<AppRouter>['timesheet']['getTimesheetsMe']['data'][number]

// Add function to calculate if overtime is possible
function isOvertimePossible(activeTimesheet: TimesheetMe, regularHours: number): boolean {
  if (!activeTimesheet?.clockIn || !activeTimesheet?.clockOut) return false

  const clockIn = new Date(activeTimesheet.clockIn)
  const clockOut = new Date(activeTimesheet.clockOut)

  // Calculate total hours worked in milliseconds
  const totalHoursWorked = (clockOut.getTime() - clockIn.getTime()) / (1000 * 60 * 60)

  // Return true if total hours worked exceeds regular hours
  return totalHoursWorked > regularHours
}

function parseAndFormatTime(
  timeStr: string | undefined | null,
  outputFormat: string = 'hh:mm a',
): string {
  if (!timeStr) return 'Not set'
  let parsed = DateTime.fromFormat(timeStr, 'HH:mm')
  if (!parsed.isValid) {
    parsed = DateTime.fromFormat(timeStr, 'HH:mm:ss')
  }
  console.log({ parsed })
  return parsed.isValid ? parsed.toFormat(outputFormat) : 'Not set'
}
